import { useEffect, useState, useMemo } from "react";
import {
  Factory,
  Warehouse,
  Plus,
  Pencil,
  Trash2,
  Search,
  CheckCircle2,
  XCircle,
  X,
  Phone,
  Mail,
  MapPin,
  Building2,
  AlertCircle,
  ChevronRight,
  User,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdmin, canManageProductionResources } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";
import { MetricBox } from "./Production";

export default function ProductionResources({ user }) {
  const [activeTab, setActiveTab] = useSavedState("production_resources", "activeTab", "vendors"); // "vendors" | "warehouses"
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [productionUsers, setProductionUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useSavedState("production_resources", "search", "");
  const [statusFilter, setStatusFilter] = useSavedState("production_resources", "statusFilter", "");

  // Modals
  const [vendorModal, setVendorModal] = useState(null); // { mode: "add" | "edit", data: {...} }
  const [warehouseModal, setWarehouseModal] = useState(null); // { mode: "add" | "edit", data: {...} }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: "vendor" | "warehouse", item: {...} }
  const [submitting, setSubmitting] = useState(false);

  const canManage = canManageProductionResources(user);

  // Load resources
  const loadResources = async () => {
    setLoading(true);
    try {
      const [vRes, wRes, uRes] = await Promise.all([
        api("get", "/vendors"),
        api("get", "/warehouses"),
        api("get", "/users?active_only=true").catch(() => ({ data: [] })),
      ]);
      setVendors(vRes.data || []);
      setWarehouses(wRes.data || []);

      const allActive = uRes.data || [];
      const prodEligible = allActive.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          (
            (u.department || "").toLowerCase() === "production" ||
            (u.departments || []).some((d) => d.toLowerCase() === "production") ||
            (u.role === "admin" && (u.department || "").toLowerCase() === "production")
          )
      );
      setProductionUsers(prodEligible);
    } catch (err) {
      console.error("Failed to load production resources:", err);
      toast.error("Failed to load vendors and warehouses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResources();
  }, []);

  // Filtered lists
  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => {
      if (statusFilter && v.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return (
        (v.name || "").toLowerCase().includes(q) ||
        (v.contact_person || "").toLowerCase().includes(q) ||
        (v.email || "").toLowerCase().includes(q) ||
        (v.phone || "").toLowerCase().includes(q) ||
        (v.address || "").toLowerCase().includes(q) ||
        (v.gstin || "").toLowerCase().includes(q)
      );
    });
  }, [vendors, statusFilter, search]);

  const filteredWarehouses = useMemo(() => {
    return warehouses.filter((w) => {
      if (statusFilter && w.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      const hasUserMatch = (w.assigned_production_users || []).some(
        (u) => (typeof u === "string" ? u : u.name || "").toLowerCase().includes(q)
      );
      return (
        (w.name || "").toLowerCase().includes(q) ||
        (w.location || "").toLowerCase().includes(q) ||
        (w.contact_person || "").toLowerCase().includes(q) ||
        (w.email || "").toLowerCase().includes(q) ||
        (w.phone || "").toLowerCase().includes(q) ||
        hasUserMatch
      );
    });
  }, [warehouses, statusFilter, search]);

  const resourceMetrics = useMemo(() => {
    const activeVendors = vendors.filter((v) => v.status === "Active").length;
    const activeWarehouses = warehouses.filter((w) => w.status === "Active").length;
    return {
      totalVendors: vendors.length,
      activeVendors,
      totalWarehouses: warehouses.length,
      activeWarehouses,
    };
  }, [vendors, warehouses]);

  const vendorPagination = usePagination(filteredVendors, 10, { sectionKey: "production_resources_vendors" });
  const warehousePagination = usePagination(filteredWarehouses, 10, { sectionKey: "production_resources_warehouses" });

  // Toggle Vendor Status
  const handleToggleVendorStatus = async (vendor) => {
    const nextStatus = vendor.status === "Active" ? "Inactive" : "Active";
    try {
      await api("patch", `/vendors/${vendor.vendor_id}/status`, { status: nextStatus });
      toast.success(`Vendor '${vendor.name}' marked as ${nextStatus}`);
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    }
  };

  // Toggle Warehouse Status
  const handleToggleWarehouseStatus = async (warehouse) => {
    const nextStatus = warehouse.status === "Active" ? "Inactive" : "Active";
    try {
      await api("patch", `/warehouses/${warehouse.warehouse_id}/status`, { status: nextStatus });
      toast.success(`Warehouse '${warehouse.name}' marked as ${nextStatus}`);
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    }
  };

  // Submit Vendor
  const handleSubmitVendor = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const form = vendorModal.data;
    try {
      if (vendorModal.mode === "add") {
        await api("post", "/vendors", form);
        toast.success(`Vendor '${form.name}' added successfully`);
      } else {
        await api("patch", `/vendors/${form.vendor_id}`, form);
        toast.success(`Vendor '${form.name}' updated successfully`);
      }
      setVendorModal(null);
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Warehouse
  const handleSubmitWarehouse = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const form = { ...warehouseModal.data };
    form.assigned_production_users = form.assigned_production_users || [];
    form.assigned_user_ids = form.assigned_production_users.map((u) =>
      typeof u === "string" ? u : u.user_id
    );
    try {
      if (warehouseModal.mode === "add") {
        await api("post", "/warehouses", form);
        toast.success(`Warehouse '${form.name}' added successfully`);
      } else {
        await api("patch", `/warehouses/${form.warehouse_id}`, form);
        toast.success(`Warehouse '${form.name}' updated successfully`);
      }
      setWarehouseModal(null);
      loadResources();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save warehouse");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete Confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setSubmitting(true);
    const { type, item } = deleteConfirm;
    try {
      if (type === "vendor") {
        await api("delete", `/vendors/${item.vendor_id}`);
        toast.success(`Vendor '${item.name}' deleted successfully`);
      } else {
        await api("delete", `/warehouses/${item.warehouse_id}`);
        toast.success(`Warehouse '${item.name}' deleted successfully`);
      }
      setDeleteConfirm(null);
      loadResources();
    } catch (err) {
      const msg = err.response?.data?.detail || "Cannot delete resource";
      toast.error(msg, { duration: 6000 });
      setDeleteConfirm(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Informational notice for regular production users */}
      {!canManage && (
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 8,
            padding: "10px 16px",
            fontSize: 13,
            color: "#1e40af",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <AlertCircle size={16} color="#2563eb" style={{ flexShrink: 0 }} />
          <span>
            Production directory view: You can view available vendors and warehouses to select when handling production orders. Administrative modifications are managed by Admins.
          </span>
        </div>
      )}

      {/* Summary Metrics */}
      <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
        <MetricBox
          label="Total Vendors"
          value={resourceMetrics.totalVendors}
          tone="blue"
          active={activeTab === "vendors" && !statusFilter}
          onClick={() => {
            setActiveTab("vendors");
            setStatusFilter("");
            vendorPagination.setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Active Vendors"
          value={resourceMetrics.activeVendors}
          tone="green"
          active={activeTab === "vendors" && statusFilter === "Active"}
          onClick={() => {
            setActiveTab("vendors");
            setStatusFilter(statusFilter === "Active" ? "" : "Active");
            vendorPagination.setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Total Warehouses"
          value={resourceMetrics.totalWarehouses}
          tone="blue"
          active={activeTab === "warehouses" && !statusFilter}
          onClick={() => {
            setActiveTab("warehouses");
            setStatusFilter("");
            warehousePagination.setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Active Warehouses"
          value={resourceMetrics.activeWarehouses}
          tone="green"
          active={activeTab === "warehouses" && statusFilter === "Active"}
          onClick={() => {
            setActiveTab("warehouses");
            setStatusFilter(statusFilter === "Active" ? "" : "Active");
            warehousePagination.setCurrentPage(1);
          }}
        />
      </div>

      {/* Standard Toolbar: Sub-tabs, Search, Filter, and Action Button */}
      <div className="toolbar" style={{ marginTop: 14, marginBottom: 16 }}>
        {/* Sub-tab pills */}
        <div
          style={{
            display: "inline-flex",
            background: "#f1f5f9",
            padding: "3px",
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
          }}
        >
          <button
            type="button"
            className={`tab ${activeTab === "vendors" ? "active" : ""}`}
            data-testid="tab-vendors"
            onClick={() => {
              setActiveTab("vendors");
              vendorPagination.setCurrentPage(1);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 12px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: activeTab === "vendors" ? 700 : 500,
              background: activeTab === "vendors" ? "#ffffff" : "transparent",
              color: activeTab === "vendors" ? "var(--navy)" : "#64748b",
              border: 0,
              boxShadow: activeTab === "vendors" ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
              cursor: "pointer",
            }}
          >
            <Factory size={13} />
            <span>Vendors</span>
            <span
              style={{
                fontSize: "10.5px",
                padding: "0 6px",
                borderRadius: "10px",
                background: activeTab === "vendors" ? "#eff6ff" : "#e2e8f0",
                color: activeTab === "vendors" ? "#2563eb" : "#475569",
                fontWeight: 700,
              }}
            >
              {vendors.length}
            </span>
          </button>

          <button
            type="button"
            className={`tab ${activeTab === "warehouses" ? "active" : ""}`}
            data-testid="tab-warehouses"
            onClick={() => {
              setActiveTab("warehouses");
              warehousePagination.setCurrentPage(1);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 12px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: activeTab === "warehouses" ? 700 : 500,
              background: activeTab === "warehouses" ? "#ffffff" : "transparent",
              color: activeTab === "warehouses" ? "var(--navy)" : "#64748b",
              border: 0,
              boxShadow: activeTab === "warehouses" ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
              cursor: "pointer",
            }}
          >
            <Warehouse size={13} />
            <span>Warehouses</span>
            <span
              style={{
                fontSize: "10.5px",
                padding: "0 6px",
                borderRadius: "10px",
                background: activeTab === "warehouses" ? "#eff6ff" : "#e2e8f0",
                color: activeTab === "warehouses" ? "#2563eb" : "#475569",
                fontWeight: 700,
              }}
            >
              {warehouses.length}
            </span>
          </button>
        </div>

        {/* Search Box */}
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder={
              activeTab === "vendors"
                ? "Search vendors by name, contact, phone…"
                : "Search warehouses by name, location, contact…"
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              vendorPagination.setCurrentPage(1);
              warehousePagination.setCurrentPage(1);
            }}
            data-testid="resource-search-input"
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setSearch("");
                vendorPagination.setCurrentPage(1);
                warehousePagination.setCurrentPage(1);
              }}
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            vendorPagination.setCurrentPage(1);
            warehousePagination.setCurrentPage(1);
          }}
          data-testid="resource-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="Active">Active Only</option>
          <option value="Inactive">Inactive Only</option>
        </select>

        {(search || statusFilter) && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={() => {
              setSearch("");
              setStatusFilter("");
              vendorPagination.setCurrentPage(1);
              warehousePagination.setCurrentPage(1);
            }}
            style={{ fontSize: 12 }}
            title="Reset all filters"
          >
            Reset Filters
          </button>
        )}

        {/* Action Button for Admins */}
        {canManage && (
          <div style={{ marginLeft: "auto" }}>
            {activeTab === "vendors" ? (
              <button
                type="button"
                className="primary-btn compact"
                data-testid="add-vendor-button"
                onClick={() =>
                  setVendorModal({
                    mode: "add",
                    data: {
                      name: "",
                      contact_person: "",
                      phone: "",
                      email: "",
                      address: "",
                      gstin: "",
                      notes: "",
                      status: "Active",
                    },
                  })
                }
              >
                <Plus size={15} /> Add Vendor
              </button>
            ) : (
              <button
                type="button"
                className="primary-btn compact"
                data-testid="add-warehouse-button"
                onClick={() =>
                  setWarehouseModal({
                    mode: "add",
                    data: {
                      name: "",
                      location: "",
                      contact_person: "",
                      phone: "",
                      email: "",
                      notes: "",
                      status: "Active",
                      assigned_production_users: [],
                    },
                  })
                }
              >
                <Plus size={15} /> Add Warehouse
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main Table Panel */}
      <section className="panel" style={{ padding: 0, overflow: "hidden" }}>
        {/* Vendors Table */}
        {activeTab === "vendors" && (
          <>
            <div className="table-wrap">
            <table className="panel-table">
              <thead>
                <tr>
                  <th style={{ width: "26%" }}>Vendor Name</th>
                  <th style={{ width: "18%" }}>Contact Person</th>
                  <th style={{ width: "19%" }}>Contact Details</th>
                  <th style={{ width: "18%" }}>Location / Address</th>
                  <th style={{ width: "9%" }}>Status</th>
                  <th style={{ width: "10%", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "48px 16px", color: "var(--muted)" }}>
                      <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        {loading ? "Loading production vendors..." : "No production vendors found"}
                      </div>
                      <span style={{ fontSize: 12.5 }}>
                        {search || statusFilter
                          ? "No vendors match your active search or status filter."
                          : "No production vendors have been added yet."}
                      </span>
                    </td>
                  </tr>
                ) : (
                  vendorPagination.paginatedItems.map((v) => (
                    <tr key={v.vendor_id} data-testid={`vendor-row-${v.vendor_id}`}>
                      <td>
                        <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13.5 }}>{v.name}</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 3 }}>
                          <span
                            style={{
                              fontSize: 11,
                              fontFamily: "'Inter', system-ui, sans-serif",
                              fontWeight: 600,
                              color: "#475569",
                              background: "#f1f5f9",
                              padding: "1px 6px",
                              borderRadius: 4,
                              letterSpacing: "0.2px",
                            }}
                          >
                            {v.vendor_id}
                          </span>
                          {v.gstin && (
                            <span style={{ fontSize: 11, color: "#64748b" }}>
                              GSTIN: <span style={{ fontWeight: 600, color: "#334155" }}>{v.gstin}</span>
                            </span>
                          )}
                        </div>
                        {v.notes && (
                          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4, fontStyle: "italic", maxWidth: 360 }}>
                            "{v.notes}"
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{v.contact_person || "—"}</div>
                      </td>
                      <td>
                        {v.phone && (
                          <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, color: "#1e293b" }}>
                            <Phone size={12} color="#64748b" /> {v.phone}
                          </div>
                        )}
                        {v.email && (
                          <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, marginTop: 3, color: "#475569" }}>
                            <Mail size={12} color="#64748b" /> {v.email}
                          </div>
                        )}
                        {!v.phone && !v.email && <span className="muted">—</span>}
                      </td>
                      <td>
                        {v.address ? (
                          <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.4 }}>
                            <MapPin size={12} color="#64748b" style={{ display: "inline", marginRight: 4, verticalAlign: "-1px" }} />
                            {v.address}
                          </div>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: 20,
                            background: v.status === "Active" ? "#ecfdf5" : "#f1f5f9",
                            color: v.status === "Active" ? "#15803d" : "#64748b",
                          }}
                        >
                          {v.status === "Active" ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                          {v.status || "Active"}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {canManage ? (
                          <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{ padding: "4px 8px", fontSize: 11 }}
                              title="Edit Vendor"
                              data-testid={`edit-vendor-${v.vendor_id}`}
                              onClick={() => setVendorModal({ mode: "edit", data: { ...v } })}
                            >
                              <Pencil size={11} /> Edit
                            </button>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{
                                padding: "4px 8px",
                                fontSize: 11,
                                color: v.status === "Active" ? "#b45309" : "#15803d",
                                borderColor: v.status === "Active" ? "#fde68a" : "#bbf7d0",
                                background: v.status === "Active" ? "#fffbeb" : "#f0fdf4",
                              }}
                              title={v.status === "Active" ? "Deactivate Vendor" : "Activate Vendor"}
                              data-testid={`toggle-vendor-${v.vendor_id}`}
                              onClick={() => handleToggleVendorStatus(v)}
                            >
                              {v.status === "Active" ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              style={{ color: "#ef4444", padding: "4px" }}
                              title="Delete Vendor"
                              data-testid={`delete-vendor-${v.vendor_id}`}
                              onClick={() => setDeleteConfirm({ type: "vendor", item: v })}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>Read-only</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={vendorPagination.currentPage}
            totalItems={vendorPagination.totalItems}
            pageSize={vendorPagination.pageSize}
            onPageChange={vendorPagination.setCurrentPage}
            onPageSizeChange={vendorPagination.setPageSize}
            itemLabel="vendors"
          />
        </>
        )}

        {/* Warehouses Table */}
        {activeTab === "warehouses" && (
          <>
            <div className="table-wrap">
            <table className="panel-table">
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Warehouse Name</th>
                  <th style={{ width: "19%" }}>Location / Address</th>
                  <th style={{ width: "18%" }}>Assigned Production Users</th>
                  <th style={{ width: "13%" }}>Contact Person</th>
                  <th style={{ width: "14%" }}>Contact Details</th>
                  <th style={{ width: "6%" }}>Status</th>
                  <th style={{ width: "8%", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWarehouses.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "48px 16px", color: "var(--muted)" }}>
                      <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        {loading ? "Loading warehouses..." : "No warehouses found"}
                      </div>
                      <span style={{ fontSize: 12.5 }}>
                        {search || statusFilter
                          ? "No warehouses match your active search or status filter."
                          : "No procurement warehouses have been added yet."}
                      </span>
                    </td>
                  </tr>
                ) : (
                  warehousePagination.paginatedItems.map((w) => (
                    <tr key={w.warehouse_id} data-testid={`warehouse-row-${w.warehouse_id}`}>
                      <td>
                        <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13.5 }}>{w.name}</div>
                        <span
                          style={{
                            fontSize: 11,
                            fontFamily: "'Inter', system-ui, sans-serif",
                            fontWeight: 600,
                            color: "#475569",
                            background: "#f1f5f9",
                            padding: "1px 6px",
                            borderRadius: 4,
                            marginTop: 3,
                            display: "inline-block",
                            letterSpacing: "0.2px",
                          }}
                        >
                          {w.warehouse_id}
                        </span>
                        {w.notes && (
                          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4, fontStyle: "italic", maxWidth: 360 }}>
                            "{w.notes}"
                          </div>
                        )}
                      </td>
                      <td>
                        {w.location ? (
                          <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.4 }}>
                            <MapPin size={12} color="#64748b" style={{ display: "inline", marginRight: 4, verticalAlign: "-1px" }} />
                            {w.location}
                          </div>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td data-testid={`warehouse-assigned-users-${w.warehouse_id}`}>
                        {w.assigned_production_users && w.assigned_production_users.length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {w.assigned_production_users.map((u, idx) => {
                              const uName = typeof u === "string" ? u : u.name || u.user_id;
                              return (
                                <div
                                  key={u.user_id || idx}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 5,
                                    background: "#ecfdf5",
                                    color: "#065f46",
                                    border: "1px solid #a7f3d0",
                                    padding: "2px 8px",
                                    borderRadius: 4,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    width: "fit-content",
                                  }}
                                  title={typeof u === "object" ? `${u.name} (${u.email || u.user_id})` : uName}
                                >
                                  <User size={12} color="#059669" />
                                  <span>{uName}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: "#f8fafc",
                              color: "#94a3b8",
                              border: "1px solid #e2e8f0",
                              padding: "2px 8px",
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 500,
                            }}
                          >
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{w.contact_person || "—"}</div>
                      </td>
                      <td>
                        {w.phone && (
                          <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, color: "#1e293b" }}>
                            <Phone size={12} color="#64748b" /> {w.phone}
                          </div>
                        )}
                        {w.email && (
                          <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5, marginTop: 3, color: "#475569" }}>
                            <Mail size={12} color="#64748b" /> {w.email}
                          </div>
                        )}
                        {!w.phone && !w.email && <span className="muted">—</span>}
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "3px 8px",
                            borderRadius: 20,
                            background: w.status === "Active" ? "#ecfdf5" : "#f1f5f9",
                            color: w.status === "Active" ? "#15803d" : "#64748b",
                          }}
                        >
                          {w.status === "Active" ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                          {w.status || "Active"}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {canManage ? (
                          <div style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{ padding: "4px 8px", fontSize: 11 }}
                              title="Edit Warehouse"
                              data-testid={`edit-warehouse-${w.warehouse_id}`}
                              onClick={() => setWarehouseModal({ mode: "edit", data: { ...w, assigned_production_users: w.assigned_production_users || [] } })}
                            >
                              <Pencil size={11} /> Edit
                            </button>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{
                                padding: "4px 8px",
                                fontSize: 11,
                                color: w.status === "Active" ? "#b45309" : "#15803d",
                                borderColor: w.status === "Active" ? "#fde68a" : "#bbf7d0",
                                background: w.status === "Active" ? "#fffbeb" : "#f0fdf4",
                              }}
                              title={w.status === "Active" ? "Deactivate Warehouse" : "Activate Warehouse"}
                              data-testid={`toggle-warehouse-${w.warehouse_id}`}
                              onClick={() => handleToggleWarehouseStatus(w)}
                            >
                              {w.status === "Active" ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              style={{ color: "#ef4444", padding: "4px" }}
                              title="Delete Warehouse"
                              data-testid={`delete-warehouse-${w.warehouse_id}`}
                              onClick={() => setDeleteConfirm({ type: "warehouse", item: w })}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>Read-only</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={warehousePagination.currentPage}
            totalItems={warehousePagination.totalItems}
            pageSize={warehousePagination.pageSize}
            onPageChange={warehousePagination.setCurrentPage}
            onPageSizeChange={warehousePagination.setPageSize}
            itemLabel="warehouses"
          />
        </>
        )}
      </section>

      {/* ========================================================================= */}
      {/* VENDOR MODAL (ADD / EDIT) - CRM NATIVE FORM-GRID MODAL                   */}
      {/* ========================================================================= */}
      {vendorModal && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmitVendor}
            style={{ width: "min(560px, 95vw)" }}
            data-testid="vendor-form-modal"
          >
            <div className="modal-head">
              <div>
                <div className="small-label">PRODUCTION RESOURCES</div>
                <h2>{vendorModal.mode === "add" ? "Add Production Vendor" : `Edit Vendor · ${vendorModal.data.name}`}</h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setVendorModal(null)}
                disabled={submitting}
              >
                <X size={18} />
              </button>
            </div>

            <div className="form-grid">
              <label>
                Vendor / Company Name <span style={{ color: "var(--red)" }}>*</span>
                <input
                  type="text"
                  placeholder="e.g. ABC Packaging Pvt. Ltd."
                  value={vendorModal.data.name || ""}
                  onChange={(e) =>
                    setVendorModal({ ...vendorModal, data: { ...vendorModal.data, name: e.target.value } })
                  }
                  required
                  data-testid="vendor-input-name"
                />
              </label>

              <label>
                Contact Person
                <input
                  type="text"
                  placeholder="e.g. Anil Sharma"
                  value={vendorModal.data.contact_person || ""}
                  onChange={(e) =>
                    setVendorModal({
                      ...vendorModal,
                      data: { ...vendorModal.data, contact_person: e.target.value },
                    })
                  }
                  data-testid="vendor-input-contact"
                />
              </label>

              <label>
                Phone Number
                <input
                  type="text"
                  placeholder="e.g. +91 98111 22334"
                  value={vendorModal.data.phone || ""}
                  onChange={(e) =>
                    setVendorModal({
                      ...vendorModal,
                      data: { ...vendorModal.data, phone: e.target.value },
                    })
                  }
                  data-testid="vendor-input-phone"
                />
              </label>

              <label>
                Email Address
                <input
                  type="email"
                  placeholder="e.g. vendor@example.com"
                  value={vendorModal.data.email || ""}
                  onChange={(e) =>
                    setVendorModal({
                      ...vendorModal,
                      data: { ...vendorModal.data, email: e.target.value },
                    })
                  }
                  data-testid="vendor-input-email"
                />
              </label>

              <label>
                GSTIN (Optional)
                <input
                  type="text"
                  placeholder="e.g. 07AAAAA0000A1Z5"
                  value={vendorModal.data.gstin || ""}
                  onChange={(e) =>
                    setVendorModal({
                      ...vendorModal,
                      data: { ...vendorModal.data, gstin: e.target.value.toUpperCase() },
                    })
                  }
                  data-testid="vendor-input-gstin"
                />
              </label>

              <label>
                Status
                <select
                  value={vendorModal.data.status || "Active"}
                  onChange={(e) =>
                    setVendorModal({
                      ...vendorModal,
                      data: { ...vendorModal.data, status: e.target.value },
                    })
                  }
                  data-testid="vendor-input-status"
                >
                  <option value="Active">Active (Available for assignment)</option>
                  <option value="Inactive">Inactive (Hidden from assignment)</option>
                </select>
              </label>
            </div>

            <label className="full">
              Address
              <input
                type="text"
                placeholder="Factory or office address..."
                value={vendorModal.data.address || ""}
                onChange={(e) =>
                  setVendorModal({
                    ...vendorModal,
                    data: { ...vendorModal.data, address: e.target.value },
                  })
                }
                data-testid="vendor-input-address"
              />
            </label>

            <label className="full">
              Notes / Manufacturing Capabilities
              <input
                type="text"
                placeholder="e.g. Corrugated boxes, offset printing, mono-cartons..."
                value={vendorModal.data.notes || ""}
                onChange={(e) =>
                  setVendorModal({
                    ...vendorModal,
                    data: { ...vendorModal.data, notes: e.target.value },
                  })
                }
                data-testid="vendor-input-notes"
              />
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 10, marginTop: 18 }}>
              <button
                type="button"
                className="outline-btn wide"
                onClick={() => setVendorModal(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn wide"
                disabled={submitting || !vendorModal.data.name?.trim()}
                data-testid="save-vendor-button"
              >
                {submitting
                  ? "Saving…"
                  : vendorModal.mode === "add"
                  ? "Create Vendor"
                  : "Save Changes"}{" "}
                <ChevronRight size={16} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ========================================================================= */}
      {/* WAREHOUSE MODAL (ADD / EDIT) - CRM NATIVE FORM-GRID MODAL                */}
      {/* ========================================================================= */}
      {warehouseModal && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmitWarehouse}
            style={{ width: "min(560px, 95vw)" }}
            data-testid="warehouse-form-modal"
          >
            <div className="modal-head">
              <div>
                <div className="small-label">PRODUCTION RESOURCES</div>
                <h2>
                  {warehouseModal.mode === "add"
                    ? "Add Warehouse"
                    : `Edit Warehouse · ${warehouseModal.data.name}`}
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setWarehouseModal(null)}
                disabled={submitting}
              >
                <X size={18} />
              </button>
            </div>

            <div className="form-grid">
              <label>
                Warehouse Name <span style={{ color: "var(--red)" }}>*</span>
                <input
                  type="text"
                  placeholder="e.g. Delhi Central Warehouse"
                  value={warehouseModal.data.name || ""}
                  onChange={(e) =>
                    setWarehouseModal({ ...warehouseModal, data: { ...warehouseModal.data, name: e.target.value } })
                  }
                  required
                  data-testid="warehouse-input-name"
                />
              </label>

              <label>
                Contact Person / Manager
                <input
                  type="text"
                  placeholder="e.g. Manoj Rawat"
                  value={warehouseModal.data.contact_person || ""}
                  onChange={(e) =>
                    setWarehouseModal({
                      ...warehouseModal,
                      data: { ...warehouseModal.data, contact_person: e.target.value },
                    })
                  }
                  data-testid="warehouse-input-contact"
                />
              </label>

              <label>
                Phone Number
                <input
                  type="text"
                  placeholder="e.g. +91 98444 55667"
                  value={warehouseModal.data.phone || ""}
                  onChange={(e) =>
                    setWarehouseModal({
                      ...warehouseModal,
                      data: { ...warehouseModal.data, phone: e.target.value },
                    })
                  }
                  data-testid="warehouse-input-phone"
                />
              </label>

              <label>
                Email Address
                <input
                  type="email"
                  placeholder="e.g. warehouse.hub@pinakisolutions.com"
                  value={warehouseModal.data.email || ""}
                  onChange={(e) =>
                    setWarehouseModal({
                      ...warehouseModal,
                      data: { ...warehouseModal.data, email: e.target.value },
                    })
                  }
                  data-testid="warehouse-input-email"
                />
              </label>

              <label>
                Status
                <select
                  value={warehouseModal.data.status || "Active"}
                  onChange={(e) =>
                    setWarehouseModal({
                      ...warehouseModal,
                      data: { ...warehouseModal.data, status: e.target.value },
                    })
                  }
                  data-testid="warehouse-input-status"
                >
                  <option value="Active">Active (Available for assignment)</option>
                  <option value="Inactive">Inactive (Hidden from assignment)</option>
                </select>
              </label>

              <label>
                Notes / Capabilities
                <input
                  type="text"
                  placeholder="e.g. Regional staging depot, loading docks..."
                  value={warehouseModal.data.notes || ""}
                  onChange={(e) =>
                    setWarehouseModal({
                      ...warehouseModal,
                      data: { ...warehouseModal.data, notes: e.target.value },
                    })
                  }
                  data-testid="warehouse-input-notes"
                />
              </label>
            </div>

            <label className="full">
              Location / Facility Address
              <input
                type="text"
                placeholder="Full facility address, gate number, city, pin code..."
                value={warehouseModal.data.location || ""}
                onChange={(e) =>
                  setWarehouseModal({
                    ...warehouseModal,
                    data: { ...warehouseModal.data, location: e.target.value },
                  })
                }
                data-testid="warehouse-input-location"
              />
            </label>

            {/* Assigned Production Users (Optional, Max 2) */}
            <div className="full" style={{ marginTop: 10 }}>
              <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#1e293b", marginBottom: 5 }}>
                Assigned Production Users <span style={{ color: "#64748b", fontWeight: 400, fontSize: 11.5 }}>(Optional — Select up to 2)</span>
              </label>
              <WarehouseProductionUserMultiSelect
                users={productionUsers}
                selectedUsers={warehouseModal.data.assigned_production_users || []}
                onChange={(updated) =>
                  setWarehouseModal({
                    ...warehouseModal,
                    data: { ...warehouseModal.data, assigned_production_users: updated },
                  })
                }
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 10, marginTop: 18 }}>
              <button
                type="button"
                className="outline-btn wide"
                onClick={() => setWarehouseModal(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn wide"
                disabled={submitting || !warehouseModal.data.name?.trim()}
                data-testid="save-warehouse-button"
              >
                {submitting
                  ? "Saving…"
                  : warehouseModal.mode === "add"
                  ? "Create Warehouse"
                  : "Save Changes"}{" "}
                <ChevronRight size={16} />
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ========================================================================= */}
      {/* DELETE CONFIRMATION DIALOG                                                */}
      {/* ========================================================================= */}
      {deleteConfirm && (
        <div className="modal-backdrop" style={{ zIndex: 10000 }}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 440, width: "95%", padding: 24 }}
            data-testid="delete-confirm-dialog"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#fef2f2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={22} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: "#111827" }}>
                  Delete {deleteConfirm.type === "vendor" ? "Vendor" : "Warehouse"}?
                </h3>
                <p className="muted" style={{ margin: "2px 0 0", fontSize: 12 }}>
                  Historical order preservation check will be performed.
                </p>
              </div>
            </div>

            <p style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, margin: "14px 0" }}>
              Are you sure you want to delete <b>{deleteConfirm.item.name}</b>? If this resource is linked to past orders, deletion will be safely prevented to preserve historical tracking (you can deactivate it instead).
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 10, marginTop: 18 }}>
              <button
                type="button"
                className="outline-btn wide"
                onClick={() => setDeleteConfirm(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn wide"
                style={{ background: "#dc2626", borderColor: "#dc2626" }}
                onClick={handleDeleteConfirm}
                disabled={submitting}
                data-testid="confirm-delete-button"
              >
                {submitting ? "Deleting…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==============================================================================
// Multi-Select Dropdown Component for Warehouse Production Users (Max 2)
// ==============================================================================
function WarehouseProductionUserMultiSelect({ users, selectedUsers, onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  const isSelected = (u) => {
    return (selectedUsers || []).some((item) => {
      if (typeof item === "string") return item === u.user_id || item === u.name;
      return item.user_id === u.user_id || (item.name && item.name.toLowerCase() === u.name.toLowerCase());
    });
  };

  const handleToggle = (u) => {
    const selected = isSelected(u);
    if (selected) {
      const filtered = (selectedUsers || []).filter((item) => {
        if (typeof item === "string") return item !== u.user_id && item !== u.name;
        return item.user_id !== u.user_id && item.name?.toLowerCase() !== u.name.toLowerCase();
      });
      onChange(filtered);
    } else {
      if ((selectedUsers || []).length >= 2) {
        toast.info("A warehouse can have a maximum of 2 assigned production users.");
        return;
      }
      const newEntry = {
        user_id: u.user_id,
        name: u.name,
        email: u.email || "",
      };
      onChange([...(selectedUsers || []), newEntry]);
    }
  };

  const handleRemove = (item, e) => {
    e.stopPropagation();
    const itemId = typeof item === "string" ? item : item.user_id;
    const itemName = typeof item === "string" ? item : item.name;
    const filtered = (selectedUsers || []).filter((u) => {
      if (typeof u === "string") return u !== itemId && u !== itemName;
      return u.user_id !== itemId && u.name?.toLowerCase() !== itemName?.toLowerCase();
    });
    onChange(filtered);
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          background: "#ffffff",
          minHeight: 38,
          padding: "5px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          gap: 6,
        }}
        data-testid="assigned-production-users-selector"
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", flex: 1 }}>
          {selectedUsers && selectedUsers.length > 0 ? (
            selectedUsers.map((item, idx) => {
              const uName = typeof item === "string" ? item : item.name || item.user_id;
              return (
                <span
                  key={idx}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    background: "#ecfdf5",
                    color: "#065f46",
                    border: "1px solid #a7f3d0",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontSize: 11.5,
                    fontWeight: 600,
                  }}
                  data-testid={`selected-user-tag-${idx}`}
                >
                  <User size={12} color="#059669" />
                  <span>{uName}</span>
                  <button
                    type="button"
                    onClick={(e) => handleRemove(item, e)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      color: "#059669",
                      marginLeft: 2,
                    }}
                    title="Remove user"
                    data-testid={`remove-assigned-user-${idx}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12.5 }}>
              -- Select Production Users (Optional, max 2) --
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#64748b", flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>{(selectedUsers || []).length}/2</span>
          <ChevronDown size={14} style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
            zIndex: 10005,
            maxHeight: 220,
            overflowY: "auto",
          }}
          data-testid="assigned-production-users-dropdown"
        >
          {users.length === 0 ? (
            <div style={{ padding: "12px", color: "#64748b", fontSize: 12, textAlign: "center" }}>
              No active production users found.
            </div>
          ) : (
            <div>
              {users.map((u) => {
                const checked = isSelected(u);
                const disabled = !checked && (selectedUsers || []).length >= 2;

                return (
                  <div
                    key={u.user_id}
                    onClick={() => !disabled && handleToggle(u)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 12px",
                      cursor: disabled ? "not-allowed" : "pointer",
                      background: checked ? "#f0fdf4" : "#ffffff",
                      borderBottom: "1px solid #f1f5f9",
                      opacity: disabled ? 0.5 : 1,
                      transition: "background 0.15s",
                    }}
                    data-testid={`checkbox-user-${u.user_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => {}}
                      style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: checked ? "#15803d" : "#1e293b" }}>
                        {u.name}
                      </div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        {u.department || "Production"}{u.email ? ` · ${u.email}` : ""}
                      </div>
                    </div>
                    {disabled && (
                      <span style={{ fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>
                        Max 2 selected
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div
            style={{
              padding: "6px 12px",
              background: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 11,
              color: "#64748b",
            }}
          >
            <span>Maximum 2 users can be assigned</span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              style={{
                background: "none",
                border: "none",
                color: "#2563eb",
                fontWeight: 600,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
